// src/Utils/Scheduler.ts
import { NoteType } from '@museflow-ai/opensheetmusicdisplay';
import { TickNoteEvent } from 'Utils/CustomEvents';
import { TICK_EVT_NAME } from 'Utils/Constants';
import { noteTypeToDivisionsPerMeasure, noteTypeToDivisionsIn4Over4 } from 'Utils';
import SHA256 from 'crypto-js/sha256';
import ITimeKeeper from 'Models/ITimeKeeper';
import * as Tone from 'tone';
import TimeKeeper from 'Models/TimeKeeper';
import { find } from 'lodash';
import { v4 as uuid } from 'uuid';
import logger from 'Utils/Logger';
import { DOWNBEAT_B64, UPBEAT_B64 } from 'assets/audio/clicks/base64';
import { decodeBase64ToAudioBuffer } from './audioBuffers';
import { awsRum } from 'Utils/AwsRum';
import type { PluginListenerHandle } from '@capacitor/core';
import { isNativeTimeKeeper } from 'Models/createTimeKeeper';
import type NativeTimeKeeper from 'Models/NativeTimeKeeper';
import MuseFlowAudio from 'Utils/MuseFlowAudioPlugin';
import { notifyAppAudioOutput } from 'AudioRecognition/appAudioSuppression';

const LOWEST_START_MEASURE = -1;

export type IntervalFn = {
  name: string; // helpful for debugging
  fn: (evt: typeof TickNoteEvent) => void;
  intervalNoteType: NoteType;
  numInterval: number;
  noteOffset: number;
  lastInvocationTick: number;
  id: string;
};

export type TimeoutFunctionTick = {
  name: string; // helpful for debugging
  fn: () => void;
  count: number;
  countNoteType: NoteType;
  lastUpdateTick: number;
  ticksSinceScheduled?: number;
  id: string;
};

export type PauseFn = (countInTimestamp: number) => void;
export type UnpauseFn = (
  countInTicks: number,
  playbackTicks: number,
  ticksPerMeasure: number,
) => void;

/*const downbeatSample = new Tone.Player(
  process.env.PUBLIC_URL + '/' + 'Synth_Sine_C_hi.wav'
).toDestination();
const upBeatSample = new Tone.Player(
  process.env.PUBLIC_URL + '/' + 'Synth_Sine_C_lo.wav'
).toDestination();*/
class Scheduler {
  intervalFns: IntervalFn[] = [];
  timeoutFns: TimeoutFunctionTick[] = [];
  pauseFns: PauseFn[] = [];
  unpauseFns: UnpauseFn[] = [];
  lastUnpauseMeasure: number = -1;
  lastPauseMeasure: number = -1;

  timeKeeper: ITimeKeeper;
  downBeatSample: Tone.Player | undefined;
  //  = new Tone.Player(process.env.PUBLIC_URL + "/" + "Synth_Sine_C_hi.wav").toDestination()
  upBeatSample: Tone.Player | undefined;
  //  = new Tone.Player(process.env.PUBLIC_URL + "/" + "Synth_Sine_C_lo.wav").toDestination()
  // downBeatSample: Tone.Synth | undefined;
  // upBeatSample: Tone.Synth | undefined;
  workerState: object = {};
  lastScheduledBeat = -10000;
  handlingPauseOrUnpause = false;
  audioInitialized = false;
  id = uuid();

  repMetronomeSound = true;
  repDownbeatsSound = true;
  countInMeasures = LOWEST_START_MEASURE;
  pickUpMeasureOffset = 0;

  tickIntervalFn: NodeJS.Timeout | undefined;
  private nativeTickListener: PluginListenerHandle | undefined;
  private _lastNativeTick: number = -1;
  private _nativeTicksSinceResume: number = 0;
  private loopStartTimestamp: number | null = null;
  private loopEndTimestamp: number | null = null;

  constructor(
    timeKeeper: ITimeKeeper,
    countInMeasures = LOWEST_START_MEASURE,
    pickUpMeasureOffset: number = 0,
  ) {
    this.timeKeeper = timeKeeper;
    // const pickUpMeasureLength = phrases[0].pickUpMeasureLength
    // const pickUpOffset = (phrases[0].musicXML.timeSignatures[0].numerator / phrases[0].musicXML.timeSignatures[0].denominator) - pickUpMeasureLength
    this.countInMeasures = countInMeasures;
    this.lastPauseMeasure = this.countInMeasures;
    this.pickUpMeasureOffset = pickUpMeasureOffset;
  }

  printUuid = () => {
    logger.info(this.id);
  };

  updateWorkerState = (newstate: object) => {
    Object.assign(this.workerState, newstate);
  };

  reset = () => {
    this.timeKeeper.resetTime();
    for (const pauseCb of this.pauseFns) {
      pauseCb(0);
    }
    this.intervalFns = [];
    this.timeoutFns = [];
    this.lastUnpauseMeasure = -1;
    this.lastPauseMeasure = -1;
    this._lastNativeTick = -1;
    this._nativeTicksSinceResume = 0;
    clearInterval(this.tickIntervalFn);
    if (isNativeTimeKeeper(this.timeKeeper)) {
      this.stopNativeScheduler();
    }
    this.removeNativeTickListener();
    if (this._tickEventListenerBound) {
      self.removeEventListener(TICK_EVT_NAME, this._tickEventListenerBound);
      this._tickEventListenerBound = undefined;
    }
    logger.debug('clear interval fn', this.tickIntervalFn);
  };

  /** Remove all listeners so the instance can be garbage collected. */
  destroy = () => {
    this.reset();
    this.pauseFns = [];
    this.unpauseFns = [];
  };

  private removeNativeTickListener = () => {
    if (this.nativeTickListener) {
      this.nativeTickListener.remove();
      this.nativeTickListener = undefined;
    }
  };

  /**
   * Reset the scheduler for replay functionality.
   * Updates count-in measures and pickup offset, resets all internal state,
   * but keeps the same scheduler instance so references remain valid.
   */
  resetForReplay = (countInMeasures: number, pickUpMeasureOffset: number) => {
    this.timeKeeper.resetTime();
    // Stop any running tick intervals
    clearInterval(this.tickIntervalFn);
    if (isNativeTimeKeeper(this.timeKeeper)) {
      this.stopNativeScheduler();
    }
    this.tickIntervalFn = undefined;
    // Reset all internal state
    this.intervalFns = [];
    this.timeoutFns = [];
    this.lastUnpauseMeasure = -1;
    this.lastPauseMeasure = countInMeasures;
    this.lastScheduledBeat = -10000;
    this._lastNativeTick = -1;
    this._nativeTicksSinceResume = 0;
    this.workerState = {};
    // Update count-in values (normally only set in constructor)
    this.countInMeasures = countInMeasures;
    this.pickUpMeasureOffset = pickUpMeasureOffset;
    // Note: We intentionally do NOT clear pauseFns/unpauseFns here
    // because the Staff component's callbacks should remain registered
    logger.info('Scheduler.resetForReplay: Reset complete with countInMeasures=', countInMeasures);
  };

  cleatPauseUnpauseCallbacks = () => {
    this.pauseFns = [];
    this.unpauseFns = [];
  };

  pauseTicks = (goBackOnPause: boolean = true) => {
    logger.debug(
      '[Scheduler.pauseTicks] called, isPaused=',
      this.timeKeeper?.isPaused(),
      'handlingPauseOrUnpause=',
      this.handlingPauseOrUnpause,
      'isNative=',
      isNativeTimeKeeper(this.timeKeeper),
    );
    if (this.timeKeeper && !this?.timeKeeper.isPaused() && !this.handlingPauseOrUnpause) {
      console.log(
        `[LOOP-DBG] Scheduler.pauseTicks PROCEEDING — clearing interval ${this.tickIntervalFn}`,
      );
      this.handlingPauseOrUnpause = true;
      this.timeKeeper.pause();
      const elapsedTicks = this.timeKeeper?.calculateElapsedTicks();
      const numberOfTicksPerMeasure = this.timeKeeper.getTicksPerMeasure();
      clearInterval(this.tickIntervalFn);
      // Null the handle after clearing. unpauseTicks's loop-bounds branch
      // recreates the tick interval only when `!this.tickIntervalFn`; leaving
      // a stale id here makes it skip recreation, so the metronome never
      // restarts after pause → enable looping → play.
      this.tickIntervalFn = undefined;
      if (isNativeTimeKeeper(this.timeKeeper)) {
        this.stopNativeScheduler();
      }
      logger.debug('clear interval fn', this.tickIntervalFn);
      let countInMeasures2;
      if (goBackOnPause) {
        // let countInMeasures2 = (Math.floor(elapsedTicks / numberOfTicksPerMeasure) - 1)
        countInMeasures2 = Math.floor(elapsedTicks / numberOfTicksPerMeasure) - 1;
        //  * (1 / this.timeKeeper.getTimeSigFract())

        if (countInMeasures2 < 0) {
          countInMeasures2 = this.countInMeasures;
        }
        if (countInMeasures2 === 0 && this.pickUpMeasureOffset > 0) {
          countInMeasures2 =
            (countInMeasures2 - this.pickUpMeasureOffset) * (1 / this.timeKeeper.getTimeSigFract());
        } else {
          countInMeasures2 =
            Math.floor(elapsedTicks / numberOfTicksPerMeasure) -
            1 -
            this.pickUpMeasureOffset * (1 / this.timeKeeper.getTimeSigFract());
        }
        if (countInMeasures2 < this.lastPauseMeasure) {
          countInMeasures2 = this.lastPauseMeasure;
        }
        for (const pauseCb of this.pauseFns) {
          pauseCb(countInMeasures2 * this.timeKeeper.measureDurationAt(countInMeasures2));
        }
      } else {
        countInMeasures2 = Math.floor(elapsedTicks / numberOfTicksPerMeasure);
      }
      this.lastPauseMeasure = countInMeasures2;
      if (isNativeTimeKeeper(this.timeKeeper)) {
        logger.debug(
          '[Scheduler.pauseTicks] NATIVE lastPauseMeasure=',
          this.lastPauseMeasure,
          'elapsedTicks=',
          elapsedTicks,
          'numberOfTicksPerMeasure=',
          numberOfTicksPerMeasure,
          'goBackOnPause=',
          goBackOnPause,
          'countInMeasures2=',
          countInMeasures2,
          'pickUpMeasureOffset=',
          this.pickUpMeasureOffset,
        );
      }
      this.handlingPauseOrUnpause = false;
    } else {
      // Even when we can't run the full pause flow (TK already paused),
      // clear the tick interval to stop metronome audio scheduling.
      // This handles the case where restartLoop() paused the TK and then
      // the user presses pause — pauseTicks bails on the TK pause but we
      // still need to stop the metronome.
      console.log(
        `[LOOP-DBG] Scheduler.pauseTicks BAILED OUT — clearing interval anyway. isPaused=${this.timeKeeper?.isPaused()} tickIntervalFn=${this.tickIntervalFn}`,
      );
      clearInterval(this.tickIntervalFn);
      // Null the handle after clearing so unpauseTicks's loop-bounds branch
      // (which only recreates the interval when `!this.tickIntervalFn`) will
      // correctly restart the metronome on the next play.
      this.tickIntervalFn = undefined;
      // Also stop the native scheduler — on iOS, MuseFlowAudio.startPlayback()
      // runs its own metronome independently. If the TK was paused by
      // restartLoop (not by pauseTicks), the native side is still playing.
      if (isNativeTimeKeeper(this.timeKeeper)) {
        this.stopNativeScheduler();
      }
    }
  };

  initialize_samples = async () => {
    if (this.audioInitialized) return;
    // Make sure AudioContext is unlocked once from a user gesture
    await Tone.start();
    const ctx = Tone.getContext().rawContext;

    // Decode the embedded base64 → AudioBuffer (no fetch)
    const [hiBuf, loBuf] = await Promise.all([
      decodeBase64ToAudioBuffer(ctx, DOWNBEAT_B64),
      decodeBase64ToAudioBuffer(ctx, UPBEAT_B64),
    ]);

    // Build Players from buffers (works on web + Capacitor)
    this.downBeatSample = new Tone.Player(hiBuf).toDestination();
    this.upBeatSample = new Tone.Player(loBuf).toDestination();

    // Still slightly quieter for some reason...

    // Set levels in dB to taste
    // this.downBeatSample.volume.value = -6;
    // this.upBeatSample.volume.value   = -10;

    this.audioInitialized = true;
  };

  unpauseTicks = (
    bpm: number,
    countInMeasures: number,
    startMeasure: number,
    tickNoteType: NoteType,
  ): Promise<void> | void => {
    logger.debug(
      '[Scheduler.unpauseTicks] called, isPaused=',
      this.timeKeeper.isPaused(),
      'handlingPauseOrUnpause=',
      this.handlingPauseOrUnpause,
      'isNative=',
      isNativeTimeKeeper(this.timeKeeper),
      'lastPauseMeasure=',
      this.lastPauseMeasure,
      'bpm=',
      bpm,
      'startMeasure=',
      startMeasure,
    );
    if (!this.timeKeeper.isPaused() || this.handlingPauseOrUnpause) {
      // When loop bounds are set and TK is already running (the scene's
      // LOOP_COUNT_IN_PATH unpaused it directly), we still need to reset
      // lastScheduledBeat and ensure a tick interval exists. Without this,
      // the metronome stops after pause → bracket change → play because
      // pauseTicks cleared the interval and nothing recreates it.
      if (!this.timeKeeper.isPaused() && this.loopStartTimestamp !== null) {
        console.log(
          `[LOOP-DBG] Scheduler.unpauseTicks — TK already running with loop bounds, resetting beat counter and ensuring tick interval`,
        );
        this.lastScheduledBeat = -10000;
        if (!this.tickIntervalFn) {
          this.tickIntervalFn = this._startTickIntervalLoop();
          console.log(
            `[LOOP-DBG] Scheduler.unpauseTicks — created missing tickIntervalFn=${this.tickIntervalFn}`,
          );
        }
        // On iOS, also restart the native scheduler. pauseTicks stopped it
        // during loop restart, and the scene's LOOP_COUNT_IN_PATH only
        // unpauses the JS TK — the native side needs an explicit restart.
        if (isNativeTimeKeeper(this.timeKeeper)) {
          this.startNativeScheduler(bpm, 0, startMeasure, tickNoteType);
        }
      } else {
        console.log(
          `[LOOP-DBG] Scheduler.unpauseTicks BAILING OUT isPaused=${this.timeKeeper.isPaused()} handlingPauseOrUnpause=${this.handlingPauseOrUnpause}`,
        );
      }
      logger.debug(
        '[Scheduler.unpauseTicks] BAILING OUT — isPaused=',
        this.timeKeeper.isPaused(),
        'handlingPauseOrUnpause=',
        this.handlingPauseOrUnpause,
      );
      return;
    }
    this.handlingPauseOrUnpause = true;

    const elapsedTicks = this.timeKeeper?.calculateElapsedTicks();
    const numberOfTicksPerMeasure = this.timeKeeper.getTicksPerMeasure();

    let lastCompleteMeasure = Math.floor(elapsedTicks / numberOfTicksPerMeasure);
    if (isNaN(lastCompleteMeasure) || lastCompleteMeasure < 0) {
      lastCompleteMeasure = this.countInMeasures;
    }
    logger.debug(
      '[Scheduler.unpauseTicks] proceeding: lastPauseMeasure=',
      this.lastPauseMeasure,
      'elapsedTicks=',
      elapsedTicks,
    );

    // Reset lastInvocationTick on all interval functions so the gapCheck
    // doesn't block them after resume (stale ticks from the previous session).
    this.resetIntervalFns();

    if (isNativeTimeKeeper(this.timeKeeper)) {
      // Native path: startNativeScheduler owns the unpause + start sequence
      // because it must fetch native time before calling unpause().
      // Return the promise so callers can await native scheduler completion
      // before processing queued MIDI events (fixes first-note-always-miss).
      //
      // Apply the same loop-bounds-aware start measure as the non-native path.
      // Without this, the native scheduler starts at lastPauseMeasure (e.g., 0)
      // instead of the loop start (e.g., 1), causing a one-measure delay.
      const nativeMeasureDur = this.timeKeeper.measureDurationAt(0);
      const nativeLoopStartMeasure =
        this.loopStartTimestamp !== null && nativeMeasureDur > 0
          ? this.loopStartTimestamp / nativeMeasureDur
          : 0;
      const nativeLoopEndMeasure =
        this.loopEndTimestamp !== null && nativeMeasureDur > 0
          ? this.loopEndTimestamp / nativeMeasureDur
          : Infinity;
      const nativePausedWithinLoop =
        this.loopStartTimestamp !== null &&
        this.lastPauseMeasure > nativeLoopStartMeasure &&
        this.lastPauseMeasure < nativeLoopEndMeasure;
      const nativeEffectiveStart = nativePausedWithinLoop
        ? this.lastPauseMeasure
        : this.loopStartTimestamp !== null
          ? nativeLoopStartMeasure
          : this.lastPauseMeasure;
      logger.debug(
        `[MUS119] [Scheduler.unpauseTicks] NATIVE start — lastPauseMeasure=${
          this.lastPauseMeasure
        } effectiveStart=${nativeEffectiveStart} intervalFns=[${this.intervalFns
          .map((f) => `${f.name}:lastTick=${f.lastInvocationTick}`)
          .join(',')}]`,
      );
      return this.startNativeScheduler(bpm, 0, nativeEffectiveStart, tickNoteType)
        .then(() => {
          for (const unpauseCb of this.unpauseFns) {
            unpauseCb(
              this.timeKeeper?.calculateElapsedTicks(),
              this.timeKeeper?.getPlaybackStartTicks(),
              this.timeKeeper?.getTicksPerMeasure(),
            );
          }
          this.handlingPauseOrUnpause = false;
        })
        .catch((err: unknown) => {
          logger.error('[Scheduler.unpauseTicks] native scheduler failed:', err);
          this.handlingPauseOrUnpause = false;
          awsRum?.recordError(err instanceof Error ? err : new Error(String(err)));
        });
    } else {
      // When loop bounds are set, the start measure depends on the situation:
      // - Initial play / loop restart: use loopStartTimestamp (bracket start)
      // - Pause/resume mid-loop: use lastPauseMeasure (cursor's paused position)
      // We distinguish these by checking if lastPauseMeasure falls within the
      // loop range — if so, the user paused mid-loop and we should resume there.
      const measureDur = this.timeKeeper.measureDurationAt(0);
      const loopStartMeasure =
        this.loopStartTimestamp !== null && measureDur > 0
          ? this.loopStartTimestamp / measureDur
          : 0;
      const loopEndMeasure =
        this.loopEndTimestamp !== null && measureDur > 0
          ? this.loopEndTimestamp / measureDur
          : Infinity;
      const pausedWithinLoop =
        this.loopStartTimestamp !== null &&
        this.lastPauseMeasure > loopStartMeasure &&
        this.lastPauseMeasure < loopEndMeasure;
      const effectiveStartMeasure = pausedWithinLoop
        ? this.lastPauseMeasure
        : this.loopStartTimestamp !== null
          ? loopStartMeasure
          : this.lastPauseMeasure;
      console.log(
        `[LOOP-DBG] Scheduler.unpauseTicks non-native path — loopStartTimestamp=${this.loopStartTimestamp} loopStartMeasure=${loopStartMeasure} loopEndMeasure=${loopEndMeasure} lastPauseMeasure=${this.lastPauseMeasure} pausedWithinLoop=${pausedWithinLoop} effectiveStartMeasure=${effectiveStartMeasure}`,
      );
      this.timeKeeper.unpause(bpm, 0, effectiveStartMeasure, tickNoteType);
      this.lastScheduledBeat = -10000;
      // Skip immediate metronome scheduling when loop bounds are set —
      // the scene will pause the TK for loop count-in immediately after,
      // and the scene handles count-in clicks. If we schedule here, the
      // pre-scheduled audio plays over the scene's count-in.
      if (this.loopStartTimestamp === null) {
        this.scheduleMetronomeAudio();
      }
      // Clear any existing interval before starting a new one to prevent duplicates
      if (this.tickIntervalFn) {
        console.log(
          `[LOOP-DBG] Scheduler.unpauseTicks — clearing existing tickIntervalFn=${this.tickIntervalFn} before creating new one`,
        );
        clearInterval(this.tickIntervalFn);
      }
      this.tickIntervalFn = this._startTickIntervalLoop();
      console.log(`[LOOP-DBG] Scheduler.unpauseTicks — new tickIntervalFn=${this.tickIntervalFn}`);

      for (const unpauseCb of this.unpauseFns) {
        unpauseCb(
          this.timeKeeper?.calculateElapsedTicks(),
          this.timeKeeper?.getPlaybackStartTicks(),
          this.timeKeeper?.getTicksPerMeasure(),
        );
      }

      logger.debug('interval fn val', this.tickIntervalFn);
      this.handlingPauseOrUnpause = false;
    }
  };

  clearIntervalFn = (name: string | null) => {
    const before = this.intervalFns.length;
    this.intervalFns = this.intervalFns.filter((fn) => fn.name !== name);
    logger.debug(
      `[MUS119] clearIntervalFn "${name}" — removed ${
        before - this.intervalFns.length
      }, remaining=[${this.intervalFns.map((f) => f.name).join(',')}]`,
    );
  };

  resetIntervalFns = () => {
    logger.debug(
      `[MUS119] resetIntervalFns — resetting ${this.intervalFns.length} fns: [${this.intervalFns
        .map((f) => `${f.name}:lastTick=${f.lastInvocationTick}`)
        .join(',')}]`,
    );
    this.intervalFns = this.intervalFns.map((fn) => {
      fn.lastInvocationTick = -Infinity;
      return fn;
    });
  };

  resetTimeoutFns = () => {
    this.timeoutFns = this.timeoutFns.map((fn) => {
      fn.ticksSinceScheduled = 0;
      return fn;
    });
  };

  clearTimeoutFn = (id: string | null) => {
    this.timeoutFns = this.timeoutFns.filter((fn) => fn.id !== id);
  };

  /** Remove every pending tick-timeout registered under `name`. Used by the
   *  playback machine (MUS-243) to invalidate a non-loop piece-end timeout
   *  (`endRepTimeout`) when looping is engaged: that timeout is registered
   *  during a non-loop session, survives pause (pauseTicks only clears the
   *  tick interval, not timeoutFns), and would otherwise keep counting and
   *  fire mid-loop, spuriously pausing machine-driven playback. */
  clearTimeoutByName = (name: string) => {
    this.timeoutFns = this.timeoutFns.filter((fn) => fn.name !== name);
  };

  getWorkerState = () => this.workerState;

  clearWorkerState = () => {
    this.workerState = {};
  };

  getTimeKeeper = () => this.timeKeeper;

  setLoopBounds(startTimestamp: number, endTimestamp: number): void {
    console.log(
      `[LOOP-DBG] Scheduler.setLoopBounds startTimestamp=${startTimestamp} endTimestamp=${endTimestamp}`,
    );
    this.loopStartTimestamp = startTimestamp;
    this.loopEndTimestamp = endTimestamp;
  }

  clearLoopBounds(): void {
    console.log(`[LOOP-DBG] Scheduler.clearLoopBounds — resetting loop bounds to null`);
    this.loopStartTimestamp = null;
    this.loopEndTimestamp = null;
  }

  /**
   * Force-stop the metronome tick interval without the full pauseTicks flow.
   * Used when deactivating looping — the TK may already be paused, causing
   * pauseTicks to bail without clearing the interval.
   */
  forceStopTickInterval(): void {
    if (this.tickIntervalFn) {
      console.log(
        `[LOOP-DBG] Scheduler.forceStopTickInterval clearing interval ${this.tickIntervalFn}`,
      );
      clearInterval(this.tickIntervalFn);
      this.tickIntervalFn = undefined;
    } else {
      console.log(`[LOOP-DBG] Scheduler.forceStopTickInterval — no interval to clear`);
    }
  }

  getLoopStartTimestamp(): number | null {
    return this.loopStartTimestamp;
  }

  getLoopEndTimestamp(): number | null {
    return this.loopEndTimestamp;
  }

  _runIntervalFunctions = (event: typeof TickNoteEvent) => {
    // Rounding ticks down to the nearest integer
    const ticks = Math.floor(event.detail.ticks);
    const tickNoteType = event.detail.timeKeeper.getTickNoteType();
    for (const repeatingFn of this.intervalFns) {
      const numInterval = repeatingFn.numInterval;
      const intervalNotesPerMeasure = noteTypeToDivisionsPerMeasure(
        repeatingFn.intervalNoteType,
        this.timeKeeper.getTimeSignature(),
      );
      const numberOfTicksPerMeasure = noteTypeToDivisionsPerMeasure(
        tickNoteType,
        this.timeKeeper.getTimeSignature(),
      );
      const ticksPerIntervalNote = numberOfTicksPerMeasure / intervalNotesPerMeasure;
      const tickOffset = repeatingFn.noteOffset * ticksPerIntervalNote;
      const tickInterval =
        (this.timeKeeper.getTicksPerMeasure() / intervalNotesPerMeasure) * numInterval;

      const modCheck = (ticks - tickOffset) % tickInterval === 0;
      if (modCheck) {
        const gap = ticks - Math.abs(repeatingFn.lastInvocationTick);
        repeatingFn.lastInvocationTick = ticks;
        // Only fire when the gap is within one interval. When the gap is
        // larger (e.g. after pause/resume or a missed tick), skip this
        // invocation to avoid burst-firing. Because lastInvocationTick is
        // always updated, the function self-heals and resumes on the next
        // interval instead of being permanently blocked.
        if (tickInterval >= gap) {
          try {
            repeatingFn.fn(event);
          } catch (err) {
            console.error(
              `Scheduler: intervalFn "${repeatingFn.name}" threw at tick=${ticks}:`,
              err,
            );
          }
        }
      }
    }
  };

  _runTimeoutFunctions = (event: typeof TickNoteEvent) => {
    const ticks = event.detail.ticks;

    const tickNoteType = event.detail.timeKeeper.getTickNoteType();
    const newTimeoutFns: TimeoutFunctionTick[] = [];
    this.timeoutFns.forEach((toRunFn) => {
      const ticksSinceStart = toRunFn.ticksSinceScheduled || 0;
      const countNoteType = toRunFn.countNoteType;
      const count = toRunFn.count;
      const numberOfTicksPerMeasure = noteTypeToDivisionsPerMeasure(
        tickNoteType,
        this.timeKeeper.getTimeSignature(),
      );
      const numberOfCountNotesPerMeasure = noteTypeToDivisionsPerMeasure(
        countNoteType,
        this.timeKeeper.getTimeSignature(),
      );

      const ticksPerCountNote = numberOfTicksPerMeasure * (1 / numberOfCountNotesPerMeasure);
      // don't run if ticks is less than last update ticks because it means we've gone back to previously played state
      if (
        ticks > toRunFn.lastUpdateTick &&
        toRunFn.ticksSinceScheduled &&
        toRunFn.ticksSinceScheduled >= count * ticksPerCountNote
      ) {
        toRunFn.fn();
      } else {
        // don't update if ticks is less than last update ticks because it means we've gone back to previously played notes
        if (ticks > toRunFn.lastUpdateTick) {
          toRunFn.lastUpdateTick = ticks;
          toRunFn.ticksSinceScheduled = ticksSinceStart + 1;
        }
        newTimeoutFns.push(toRunFn);
      }
    });
    this.timeoutFns = newTimeoutFns;
  };

  hasTimeout = (name: string): boolean => {
    return !!find(this.timeoutFns, (fn) => fn.name === name);
  };

  hasInterval = (name: string): boolean => {
    return !!find(this.intervalFns, (fn) => fn.name === name);
  };

  printTimeouts = () => {
    logger.info(this.timeoutFns.map((fn) => fn.name));
  };

  printIntervals = () => {
    logger.info(this.intervalFns.map((fn) => fn.name));
  };

  setIntervalByTick = (
    name: string,
    fn: (event: typeof TickNoteEvent) => void,
    intervalNoteType: NoteType,
    numInterval: number,
    noteOffset: number = 0,
  ): string => {
    const intervalFn = {
      name,
      fn,
      intervalNoteType,
      numInterval,
      noteOffset,
      lastInvocationTick: -Infinity,
      id: SHA256(name + Date.now()).toString(),
    };
    const existingCount = this.intervalFns.filter((f) => f.name === name).length;
    if (existingCount > 0) {
      logger.warn(
        `[MUS119] setIntervalByTick DUPLICATE "${name}" — already ${existingCount} registered! Total intervalFns=[${this.intervalFns
          .map((f) => f.name)
          .join(',')}]`,
      );
    }
    logger.debug(
      `[MUS119] setIntervalByTick "${name}" intervalNoteType=${intervalNoteType} numInterval=${numInterval} noteOffset=${noteOffset} — total after: ${
        this.intervalFns.length + 1
      } fns=[${[...this.intervalFns, intervalFn].map((f) => f.name).join(',')}]`,
    );
    this.intervalFns.push(intervalFn);
    return intervalFn.id;
  };

  setTimeoutByTick = (
    name: string,
    fn: () => void,
    countNoteType: NoteType,
    count: number,
  ): string => {
    // It's possible there could be a race condition where the loop in _runScheduledFunctions starts
    // and the this function is lost because it wasn't added to the array before the loop stated and
    // therefore it wasn't added to "newToRunFns". But I'm not sure if that's possible without threading.
    const timeoutFn = {
      name,
      fn,
      count,
      countNoteType,
      lastUpdateTick: 0,
      ticksSinceScheduled: 0,
      id: SHA256(name + Date.now()).toString(),
    };
    this.timeoutFns.push(timeoutFn);
    return timeoutFn.id;
  };

  addPauseCallback = (cb: PauseFn) => {
    this.pauseFns.push(cb);
  };

  addUnpauseCallback = (cb: UnpauseFn) => {
    this.unpauseFns.push(cb);
  };

  changeDownbeatsAudio = async (setDownbeats: boolean) => {
    this.repDownbeatsSound = setDownbeats;
    if (isNativeTimeKeeper(this.timeKeeper)) {
      await MuseFlowAudio.setDownbeatsEnabled({ enabled: setDownbeats });
    }
  };

  changeMetronomeAudio = async (setMetronome: boolean) => {
    this.repMetronomeSound = setMetronome;
    if (isNativeTimeKeeper(this.timeKeeper)) {
      await MuseFlowAudio.setMetronomeEnabled({ enabled: setMetronome });
    }
  };

  scheduleMetronomeAudio = () => {
    // Don't schedule metronome audio when the TK is paused (e.g., during
    // loop count-in). The scene handles count-in clicks separately.
    if (this.timeKeeper.isPaused()) return;

    const rawStartTime = this.timeKeeper.getPlaybackStartTime();
    const timeSignatureNum = this.timeKeeper.getTimeSignature()?.numerator;
    const audioContextTime = this.timeKeeper.getAudioContextTime();
    const measureDurationSecs = this.timeKeeper.getMeasureDurationSecs();
    const ticksPerMeasure = this.timeKeeper.getTicksPerMeasure();
    // Derive startMeasure from the timekeeper's tick offset. The non-native
    // TimeKeeper stores elapsedTicksOffset as the exact product
    // ticksPerMeasure * startMeasure (no flooring), so this round-trips
    // cleanly and avoids adding a new getter to ITimeKeeper.
    const startMeasure =
      ticksPerMeasure > 0 ? this.timeKeeper.getPlaybackStartTicks() / ticksPerMeasure : 0;
    // Anchor scheduling to the music's beat 0, not to startTime. startTime is
    // the moment unpause() ran (the user's first MIDI note when resuming from
    // count-in, or the resume point on a mid-piece pause). If that note arrived
    // a fraction of a beat off the ideal tick, or mid-measure on a loop resume,
    // using startTime directly puts every subsequent click out of phase with
    // the music. Subtracting startMeasure*measureDurationSecs shifts the
    // reference back to where measure 0 of the piece would have begun, so
    // beatsElapsedSoon counts true beats since the piece's beat 0. This also
    // makes downbeat detection (beatsElapsedSoon % timeSignatureNum) correct
    // for mid-measure loop starts without a separate offset term.
    const startTime = rawStartTime - startMeasure * measureDurationSecs;
    const timeElapsed = audioContextTime - startTime;
    const secondsPerBeat = measureDurationSecs / timeSignatureNum;
    const isPaused = false; // guaranteed not paused here

    // TODO this will need extra logic for mixed meter!
    // Calculate how many beats have elapsed now, and shortly into the future.
    const beatsElapsed = Math.floor(timeElapsed / secondsPerBeat);
    const beatsElapsedSoon = Math.floor(
      (timeElapsed + TimeKeeper.METRONOME_SCHEDULE_AHEAD_TIME) / secondsPerBeat,
    );

    // If the integer portion of beatsElapsedSoon is greater than the integer portion of beatsElapsed,
    // we should schedule new beat audio.
    if (beatsElapsedSoon != this.lastScheduledBeat && beatsElapsed < beatsElapsedSoon) {
      console.log(
        `[LOOP-DBG] Scheduler.scheduleMetronomeAudio SCHEDULING BEAT isPaused=${isPaused} beatsElapsedSoon=${beatsElapsedSoon} timeElapsed=${timeElapsed.toFixed(3)} repMetronomeSound=${this.repMetronomeSound} tickIntervalFn=${this.tickIntervalFn}`,
      );
      this.lastScheduledBeat = beatsElapsedSoon;

      const scheduleTime = startTime + beatsElapsedSoon * secondsPerBeat;

      // Is this a downbeat or upbeat?
      // (The down beat is the first beat of the measure)
      if (beatsElapsedSoon % timeSignatureNum == 0 && this.repDownbeatsSound) {
        if (this.repMetronomeSound) {
          console.log(
            `[LOOP-DBG] Scheduler.scheduleMetronomeAudio — DOWNBEAT at beat ${beatsElapsedSoon} scheduleTime=${scheduleTime.toFixed(3)}`,
          );
          try {
            notifyAppAudioOutput({
              delayMs: Math.max(0, (scheduleTime - audioContextTime) * 1000),
              reason: 'metronome',
            });
            this.downBeatSample?.start(scheduleTime);
          } catch (error) {
            logger.warn('Failed to start downbeat sample:', error);
            awsRum?.recordError(error as Error);
          }
        }
      } else {
        if (this.repMetronomeSound) {
          try {
            notifyAppAudioOutput({
              delayMs: Math.max(0, (scheduleTime - audioContextTime) * 1000),
              reason: 'metronome',
            });
            this.upBeatSample?.start(scheduleTime);
          } catch (error) {
            logger.warn('Failed to start upbeat sample:', error);
            awsRum?.recordError(error as Error);
          }
        }
      }
    }
  };

  private _tickEventListenerBound: EventListener | undefined;

  _runTickEventListener = () => {
    logger.debug('[Scheduler] _runTickEventListener called, scheduler id:', this.id);
    // Remove any previously registered tick listener to prevent duplicates
    if (this._tickEventListenerBound) {
      self.removeEventListener(TICK_EVT_NAME, this._tickEventListenerBound);
    }
    this._tickEventListenerBound = ((event: typeof TickNoteEvent) => {
      this._runIntervalFunctions(event);
      this._runTimeoutFunctions(event);
    }) as EventListener;
    self.addEventListener(TICK_EVT_NAME, this._tickEventListenerBound);
  };

  _runMetronomeLoop = () => {
    this.scheduleMetronomeAudio();
  };

  _startTickIntervalLoop = (): NodeJS.Timeout => {
    let elapsedTicks = this.timeKeeper?.calculateElapsedTicks();
    const tickIntervalFn = setInterval(() => {
      // Sampled log — every ~100 ticks
      if (Math.random() < 0.01) {
        console.log(
          `[LOOP-DBG] Scheduler._tickIntervalLoop running — isPaused=${this.timeKeeper?.isPaused()} intervalId=${tickIntervalFn}`,
        );
      }
      this._runMetronomeLoop();

      // Check if elapsed ticks have changed since the last time this loop ran.
      const lastElapsedTicks = elapsedTicks;
      elapsedTicks = this.timeKeeper?.calculateElapsedTicks();

      // Run tick interval listeners if ticks have elapsed.
      if (elapsedTicks > lastElapsedTicks) {
        // Make sure we run tick events for each tick that's elapsed.
        for (let i = lastElapsedTicks + 1; i <= elapsedTicks; i++) {
          try {
            self.dispatchEvent(
              new CustomEvent(TICK_EVT_NAME, {
                detail: {
                  name: TICK_EVT_NAME,
                  ticks: i,
                  timeKeeper: this.timeKeeper,
                  workerState: this.workerState,
                },
              }),
            );
          } catch (err) {
            console.error(`Scheduler: tick dispatch threw at tick=${i}:`, err);
          }
        }
      }
    }, 25);
    return tickIntervalFn;
  };

  init = async (_workerState = {}) => {
    if (!isNativeTimeKeeper(this.timeKeeper)) {
      if (this.audioInitialized) {
        return this._runTickEventListener();
      }
      await this.initialize_samples();
      this.updateWorkerState(_workerState);
      Tone.setContext(this.timeKeeper.getAudioContext());
    } else {
      this.updateWorkerState(_workerState);
    }
    this._runTickEventListener();
  };

  runEventListeners = (_workerState = {}) => {
    this._runTickEventListener();
  };

  /**
   * Starts the native scheduler and synchronizes the JS NativeTimeKeeper's
   * startTime to the exact value computed on the native side.
   *
   * This method owns the full unpause-and-start sequence for the native path:
   * 1. Fetch the current native time to seed the NativeTimeKeeper's cache
   * 2. Call timeKeeper.unpause() so startTime is computed in the native clock domain
   * 3. Register the tick event listener
   * 4. Start native playback and get back the native startTime
   * 5. Overwrite the JS startTime with the native value for exact synchronization
   *
   * The caller must NOT call timeKeeper.unpause() before this on the native path.
   */
  /**
   * Ensures the native tick listener is registered exactly once.
   * Keeping it registered across play/pause cycles avoids the async
   * overhead of re-registering on every unpause (~10-20ms per bridge call).
   */
  private ensureNativeTickListener = async () => {
    if (this.nativeTickListener) return;

    this.nativeTickListener = await MuseFlowAudio.addListener(
      'tickEvent',
      (data: { ticks: number; currentTime: number }) => {
        if (isNativeTimeKeeper(this.timeKeeper)) {
          (this.timeKeeper as NativeTimeKeeper).updateCurrentTime(data.currentTime);
        }

        const elapsedTicks = data.ticks;
        if (elapsedTicks > this._lastNativeTick) {
          this._nativeTicksSinceResume++;
          if (this._nativeTicksSinceResume <= 3) {
            logger.debug(
              '[nativeTickListener] FIRST TICKS AFTER RESUME — tick#' +
                this._nativeTicksSinceResume,
              'elapsedTicks:',
              elapsedTicks,
              'lastNativeTick:',
              this._lastNativeTick,
              'isPaused:',
              this.timeKeeper.isPaused(),
              'currentTime:',
              data.currentTime,
            );
          }
          if (elapsedTicks % 32 === 0) {
            logger.debug(
              `[MUS119] [nativeTickListener] tick=${elapsedTicks} intervalFns=${
                this.intervalFns.length
              } [${this.intervalFns
                .map((f) => `${f.name}:lastTick=${f.lastInvocationTick}`)
                .join(',')}] timeoutFns=${this.timeoutFns.length}`,
            );
          }
          for (let i = this._lastNativeTick + 1; i <= elapsedTicks; i++) {
            self.dispatchEvent(
              new CustomEvent(TICK_EVT_NAME, {
                detail: {
                  name: TICK_EVT_NAME,
                  ticks: i,
                  timeKeeper: this.timeKeeper,
                  workerState: this.workerState,
                },
              }),
            );
          }
          this._lastNativeTick = elapsedTicks;
        }
      },
    );
  };

  startNativeScheduler = async (
    bpm: number,
    countInMeasures: number,
    startMeasure: number,
    tickNoteType: NoteType,
  ) => {
    logger.debug(
      '[startNativeScheduler] bpm=',
      bpm,
      'countIn=',
      countInMeasures,
      'startMeasure=',
      startMeasure,
      'tickNoteType=',
      tickNoteType,
    );

    // Parallelize: fetch native time AND ensure tick listener is registered.
    // getCurrentTime is needed to seed the NativeTimeKeeper before unpause.
    // The listener needs to be in place before startPlayback so no ticks are lost.
    const [{ time: nativeNow }] = await Promise.all([
      MuseFlowAudio.getCurrentTime(),
      this.ensureNativeTickListener(),
    ]);

    if (isNativeTimeKeeper(this.timeKeeper)) {
      logger.debug('[startNativeScheduler] seeding native time before unpause:', nativeNow);
      (this.timeKeeper as NativeTimeKeeper).updateCurrentTime(nativeNow);
      this.timeKeeper.unpause(bpm, countInMeasures, startMeasure, tickNoteType);
    }

    // Initialize _lastNativeTick to elapsedTicksOffset - 1 so that the JS
    // tick listener only dispatches events starting from elapsedTicksOffset
    // (the tick corresponding to startMeasure). Without this, resuming at
    // e.g. measure 4 would dispatch ticks 0–127 (for measures 0–3), causing
    // interval/timeout functions to fire for already-played measures and the
    // cursor to briefly appear at a position before the current phrase.
    const playbackStartTicks = this.timeKeeper.getPlaybackStartTicks();
    // Floor to integer: the native scheduler dispatches integer ticks
    // (Swift uses Int(startMeasure) which truncates fractional measures),
    // so _lastNativeTick must also be an integer to avoid filtering out
    // the first few native ticks when startMeasure is fractional.
    this._lastNativeTick = playbackStartTicks > 0 ? Math.floor(playbackStartTicks) - 1 : -1;
    this._nativeTicksSinceResume = 0;
    logger.debug(
      '[startNativeScheduler] _lastNativeTick=',
      this._lastNativeTick,
      'playbackStartTicks=',
      playbackStartTicks,
      'intervalFns:',
      this.intervalFns.length,
      'isPaused:',
      this.timeKeeper.isPaused(),
    );

    const startPlaybackParams = {
      bpm,
      timeSigNumerator: this.timeKeeper.getTimeSignature()?.numerator ?? 4,
      timeSigDenominator: this.timeKeeper.getTimeSignature()?.denominator ?? 4,
      countInMeasures,
      startMeasure,
      tickNoteType: noteTypeToDivisionsIn4Over4(tickNoteType),
    };
    const { startTime: nativeStartTime } = await MuseFlowAudio.startPlayback(startPlaybackParams);
    logger.debug(
      '[startNativeScheduler] startPlayback resolved, nativeStartTime=',
      nativeStartTime,
    );

    if (isNativeTimeKeeper(this.timeKeeper)) {
      (this.timeKeeper as NativeTimeKeeper).setStartTime(nativeStartTime);
    }
  };

  stopNativeScheduler = async () => {
    logger.debug('[stopNativeScheduler] called');
    // Keep the tick listener registered — the native scheduler won't dispatch
    // ticks while paused, and re-registering on every unpause adds ~10-20ms.
    try {
      await MuseFlowAudio.pausePlayback();
      logger.debug('[stopNativeScheduler] pausePlayback resolved');
    } catch (error) {
      logger.warn('Failed to pause native scheduler:', error);
    }
  };

  setTimeKeeper = (_timeKeeper: ITimeKeeper) => {
    this.timeKeeper = _timeKeeper;
  };
}

export default Scheduler;
